import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";

/**
 * Judging reviews in the admin (#260; frontend v2, "Reviews", "Beheer"; CONTEXT.md
 * `productReview`). Staff only. A review comes in `pending`; an employee approves it (published),
 * rejects it with an internal reason, or takes a published review offline again (`approved` to
 * `rejected`, with a reason). Who decided and when is recorded. Nothing here changes the buyer's
 * words: there is no procedure that edits the title, the text or the first name.
 */

/** `pending`: to judge; `approved`: published in the shop; `rejected`: never shown. */
export const ProductReviewStatus = z.enum(["pending", "approved", "rejected"]);
export type ProductReviewStatus = z.infer<typeof ProductReviewStatus>;

/** A product as the review lists name it. */
const ReviewedProduct = z.object({ id: z.string(), name: z.string() });

export const ProductReviewListItem = z.object({
	id: z.string(),
	status: ProductReviewStatus,
	stars: z.number().int(),
	title: z.string().nullable(),
	body: z.string().nullable(),
	/** As the buyer wants it shown; `null`: none, or wiped by anonymising. */
	firstName: z.string().nullable(),
	product: ReviewedProduct,
	orderId: z.string(),
	/** `CBO-100000`. */
	orderNumber: z.string(),
	writtenAt: z.iso.datetime(),
});
export type ProductReviewListItem = z.infer<typeof ProductReviewListItem>;

/**
 * `GET /admin/reviews`: the newest first, filtered on status and product, searched on title, text,
 * first name, product name and order number.
 */
export const ProductReviewListInput = listInput({
	sort: ["writtenAt"],
	defaultSort: "writtenAt",
	defaultOrder: "desc",
	filters: {
		status: ProductReviewStatus.optional().catch(undefined),
		productId: z.string().min(1).max(64).optional().catch(undefined),
	},
});
export type ProductReviewListQuery = z.infer<typeof ProductReviewListInput>;

export const ProductReviewList = listPage(ProductReviewListItem).extend({
	/** Every product that has a review, by name: the product filter's options. */
	products: z.array(ReviewedProduct),
});
export type ProductReviewList = z.infer<typeof ProductReviewList>;

/** One review with what an employee needs to judge it: its order, its customer and its product. */
export const AdminProductReview = ProductReviewListItem.extend({
	product: ReviewedProduct.extend({ slug: z.string() }),
	order: z.object({
		id: z.string(),
		orderNumber: z.string(),
		placedAt: z.iso.datetime(),
		/** The name on the order. */
		name: z.string(),
	}),
	customer: z.object({
		id: z.string(),
		/** `null` when unknown, and after anonymising. */
		name: z.string().nullable(),
		email: z.string(),
		anonymised: z.boolean(),
	}),
	/** Why it was rejected or taken offline: internal, never in the shop. */
	rejectionReason: z.string().nullable(),
	/** The last decision on it: when and by whom; `null` while `pending`. */
	reviewedAt: z.iso.datetime().nullable(),
	reviewedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type AdminProductReview = z.infer<typeof AdminProductReview>;

export const ProductReviewPath = z.object({ reviewId: z.string().min(1).max(64) });

/** Rejecting or taking offline: the internal reason, required. */
export const ProductReviewReasonBody = z.object({ reason: z.string().trim().min(1).max(500) });
export type ProductReviewReasonBody = z.infer<typeof ProductReviewReasonBody>;

/**
 * The reviews admin. Errors on one review: `ProductReviewNotFound` (404); on a decision also
 * `ReviewDecisionNotAllowed` (409): the review is not in the status the decision starts from
 * (approve and reject: `pending`; take offline: `approved`), also when someone else decided first.
 */
export const ProductReviewAdminContract = {
	list: oc
		.route({ method: "GET", path: "/admin/reviews" })
		.input(ProductReviewListInput)
		.output(ProductReviewList),
	get: oc
		.route({ method: "GET", path: "/admin/reviews/{reviewId}" })
		.input(ProductReviewPath)
		.output(AdminProductReview),
	/** `pending` to `approved`: the review is published in the shop. */
	approve: oc
		.route({ method: "POST", path: "/admin/reviews/{reviewId}/approve" })
		.input(ProductReviewPath)
		.output(AdminProductReview),
	/** `pending` to `rejected`, with an internal reason. */
	reject: oc
		.route({ method: "POST", path: "/admin/reviews/{reviewId}/reject" })
		.input(ProductReviewPath.extend(ProductReviewReasonBody.shape))
		.output(AdminProductReview),
	/** `approved` to `rejected`, with an internal reason: no longer in the shop. */
	takeOffline: oc
		.route({ method: "POST", path: "/admin/reviews/{reviewId}/take-offline" })
		.input(ProductReviewPath.extend(ProductReviewReasonBody.shape))
		.output(AdminProductReview),
};
